import React, { useCallback, useEffect, useState } from 'react'
import { useAppContext } from '@/components/AppContext'
import DragAndDropModal from '@/hoc/DragAndDropModal'
import { YODA_CDN_URL, PUJA_PRESIGN_URL, A4B_APPS } from '@/utils/constants'
import { Col, Image, Row, Tag, message } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import SectionCreation from '../SectionCreation'
import {
  CreateStore,
  EditSection,
  ManageCategoryItem,
  ManageStoreCard,
} from '@a4b/api/src/modules/Monetisation/Puja/types'
import { sortBy } from 'lodash'
import { parseCreateSectionData, parseInventoryList } from './helper'
import { showErrorToast } from '@/utils/helper'
import { staticUIItemTypes } from '../../../utils/constants'

interface Props {
  showItemCreation: boolean
  isEditJourney: boolean
  closeItemCreation: (fetchSection?: boolean | React.MouseEvent | React.KeyboardEvent) => void
  formData: EditSection | undefined
}
interface ShowStoreInfoProps {
  data?: Partial<CreateStore>
}

const ShowStoreInfo: React.FC<ShowStoreInfoProps> = React.memo(({ data }) => {
  return (
    <div
      style={{
        marginBottom: '8px',
        border: '1px solid #bfbfbf',
        padding: '12px',
        borderRadius: '4px'
      }}
    >
      <Row
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 'auto',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}
      >
        <Col span={8}>
          <span style={{ fontWeight: '500', fontSize: '14px' }}>
            {data?.title}
          </span>
        </Col>

        <Col span={4}>
          <span style={{ fontSize: '13px', color: '#666' }}>
            No of SKU's: {data?.no_of_items}
          </span>
        </Col>

        <Col span={3}>
          {data?.active ? (
            <Tag color='#00BD68'>Active</Tag>
          ) : (
            <Tag color='#FF5151'>Inactive</Tag>
          )}
        </Col>

        <Col span={6} style={{ textAlign: 'right' }}>
          <Image
            width={60}
            height={60}
            src={data?.icon_url}
            style={{ borderRadius: '4px' }}
          />
        </Col>
      </Row>

      <div
        style={{
          marginTop: '8px',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td
                style={{
                  backgroundColor: '#ffe6e6',
                  padding: '8px 12px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  color: '#d32f2f',
                  borderRight: '1px solid #ffcccc',
                  width: '30%'
                }}
              >
                Processing Date
              </td>
              <td
                style={{
                  backgroundColor: '#fff5f5',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: '500'
                }}
              >
                {data?.process_date || 'N/A'}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  backgroundColor: '#fff3e0',
                  padding: '8px 12px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  color: '#d35400',
                  borderRight: '1px solid #ffcc80',
                  width: '30%'
                }}
              >
                Slug
              </td>
              <td
                style={{
                  backgroundColor: '#fffbf5',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: '500'
                }}
              >
                {data?.slug || 'N/A'}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  backgroundColor: '#e3f2fd',
                  padding: '8px 12px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  color: '#1565c0',
                  borderRight: '1px solid #bbdefb',
                  width: '30%'
                }}
              >
                Store ID
              </td>
              <td
                style={{
                  backgroundColor: '#f8fcff',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: '500'
                }}
              >
                {data?.id || 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
})

const ShowStoreCardInfo: React.FC<ShowStoreInfoProps> = React.memo(({ data }) => {
  return (
    <Row style={{ display: 'flex', alignItems: 'center', height: 'auto', justifyContent: 'center', marginBottom: '8px', border: '1px solid #bfbfbf', padding: '5px' }}>
      <Col span={14}>{data?.title}</Col>
      <Image width={80} height={60} src={data?.icon_url} />
    </Row>
  )
})

const ShowCategoryInfo: React.FC<ShowStoreInfoProps> = React.memo(({ data }: any) => {
  return (
    <Row style={{ display: 'flex', alignItems: 'center', height: 'auto', justifyContent: 'center', marginBottom: '8px', border: '1px solid #bfbfbf', padding: '5px' }}>
      <Col span={12}>Title: {data?.title}</Col>
      <Col span={12}>Item Type: {data?.item_type}</Col>
      <Col span={12}>{data?.description}</Col>
      <Col span={12}>Template: {data?.template}</Col>
    </Row>
  )
})

const sectionDataTypes = ['info_media', 'info_media_web', 'previous_pooja_media', 'banner_items', 'today_importance', 'srimandir_services', 'glimpses_of_services', 'associated_temples', 'associated_pilgrims', 'previous_order_recommendation_card', 'banner_large', 'highlighted_pooja', 'intent_generation_banner', 'influencer_recommendation_banner'];

const CreateSection: React.FC<Props> = ({
  showItemCreation,
  closeItemCreation,
  isEditJourney,
  formData,
}) => {
  const { networkInstance, app, countryState } = useAppContext()
  const monetisationPujaApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationPujaApi : networkInstance.clientWithHeaders.monetisationAstroPujaApi

  const [editFormData, setEditFormData] = useState<any>()
  const [createItemForm] = useForm()

  const [storeList, setStoreList] = useState<any[]>([])
  const [inventoryList, setInventoryList] = useState<{ [storeId: string]: any }>({})
  const [storeCards, setStoreCards] = useState<ManageStoreCard[]>([])
  const [categories, setCategories] = useState<ManageCategoryItem[]>([])
  const [sortedSkuLists, setSortedSkuLists] = useState<ManageStoreCard[]>([])
  const [sortedCategoryList, setSortedCategoryList] = useState<ManageCategoryItem[]>([])
  const [sortedStoreLists, setSortedStoreLists] = useState<CreateStore[]>([])
  const [showDandDModal, setDandDModalVisibility] = useState(false)

  const getStoreList = useCallback(async () => {
    try {
      const storeList = await monetisationPujaApi.storesApi.getStoreList({
        limit: 1000,
        offset: 0,
      }, countryState?.countryCode, undefined,
        undefined, 'active', undefined, undefined, undefined)
      let store_list: any = storeList?.data?.data?.store_list
      setStoreList(store_list)
    } catch (error) {
      message.error({
        content: 'Error while fetching store list',
        duration: 3,
      })
      console.log(error)
    }
  }, [countryState?.countryCode, monetisationPujaApi.storesApi])

  const getStoreCards = useCallback(async () => {
    try {
      const storeCards = await monetisationPujaApi.storesApi.getStoreCards({
        limit: 1000,
        offset: 0,
        active: true,
      })

      setStoreCards(storeCards?.data?.data?.store_cards)
    } catch (error) {
      message.error({
        content: 'Error while fetching store list',
        duration: 3,
      })
      console.log(error)
    }
  }, [monetisationPujaApi.storesApi])

  const getCategories = useCallback(async () => {
    try {
      const categories = await monetisationPujaApi.storesApi.getCategories({
        limit: 1000,
        offset: 0,
      })

      setCategories(
        categories?.data?.data?.categories?.map((item: ManageCategoryItem) => ({
          ...item,
          id: item?.category_id,
        })),
      )
    } catch (error) {
      message.error({
        content: 'Error while fetching categories',
        duration: 3,
      })
      console.log(error)
    }
  }, [monetisationPujaApi.storesApi])

  const getInventoryList = useCallback(async (storeId: string) => {
    try {
      const inventoryList = await monetisationPujaApi.storesApi.getProductList(storeId)
      const parsedInventoryList = parseInventoryList(storeId, inventoryList?.data?.data?.products)
      setInventoryList((prevList) => ({
        ...prevList,
        [storeId]: parsedInventoryList,
      }))
      return parsedInventoryList
    } catch (error) {
      message.error({
        content: 'Error while fetching InventoryList',
        duration: 3,
      })
      console.log(error)
    }
  }, [monetisationPujaApi.storesApi])

  const fetchData = async (editForm: any) => {
    const sankalpBannerStoreIds = editForm?.data?.sankalp_banner_items?.map((bannerObj: { store_id: string, inventory_id: string }) => bannerObj?.store_id)

    if (sankalpBannerStoreIds && sankalpBannerStoreIds?.length > 0) {
      try {
        const inventoryResults = await Promise.all(
          sankalpBannerStoreIds?.map(async (storeId: string) => ({
            [storeId]: await getInventoryList(storeId),
          }))
        )
        setInventoryList(Object.assign({}, ...inventoryResults))
      } catch (error) {
        console.error('Error while fetching inventory lists:', error);
      }
    }
  }

  useEffect(() => {
    //skip api calls
    const sectionType = formData?.section_type ?? ''
    const dataType = formData?.data?.type ?? ''
    const excludeDataTypes = ['info_media', 'info_media_web', 'previous_pooja_media', 'banner_items', 'srimandir_services', 'glimpses_of_services', 'associated_temples', 'previous_order_recommendation_card', 'banner_large', 'highlighted_pooja', 'declarative']
    const excludeSectionTypes = ['static_ui', 'declarative']
    if (excludeSectionTypes.includes(sectionType) ||
      (sectionType === 'data' && excludeDataTypes.includes(dataType))) {
      return
    }

    getStoreList()
    getStoreCards()
    getCategories()
  }, [getStoreList, getStoreCards, getCategories, formData])

  useEffect(() => {
    if (isEditJourney && formData) {
      const offeringStoreId = storeList?.filter((store) => formData?.data?.today_importance?.offering_store_ids?.includes(store.id))?.map((item) => item?.id)
      const poojaStoreId = storeList?.filter((store) => formData?.data?.today_importance?.pooja_store_ids?.includes(store.id))?.map((item) => item?.id)

      const editForm = {
        ...formData,
        title: formData?.title,
        description: formData?.description,
        item_type: formData?.item_type,
        section_type: formData?.section_type,
        variant: formData?.variant,
        position: formData?.position,
        id: formData?.id,
        ...(formData?.data?.type === 'today_importance' && {
          data: {
            ...formData?.data,
            today_importance: {
              ...formData?.data?.today_importance,
              offering_store_ids: offeringStoreId,
              pooja_store_ids: poojaStoreId
            }
          }
        })
      }

      let section_type = createItemForm.getFieldValue('section_type')
      if ((section_type === 'hero' || section_type === 'list_item_small' || section_type === 'list_item_large') && formData?.item_type === 'sku') {
        const filteredStoreCard: any = storeCards?.filter(
          (storeCard: ManageStoreCard) =>
            formData?.data?.store_ids?.find((card: any) => card === storeCard.id),
        )
        const sortedItems = sortBy(filteredStoreCard, (item: ManageStoreCard) => formData?.data?.store_ids?.indexOf(item?.id))
        setSortedSkuLists(sortedItems)
        setSortedStoreLists([])
      }

      if ((section_type === 'hero' || section_type === 'list_item_small' || section_type === 'list_item_large') && formData?.item_type === 'store') {
        let tempFilteredStoreList: any = storeList?.filter((store: CreateStore) =>
          formData?.data?.store_ids?.find((card: any) => card === store.id),
        )
        const filteredStoreList = sortBy(tempFilteredStoreList, function (item) {
          return formData?.data?.store_ids?.indexOf(item.id)
        });

        setSortedStoreLists(filteredStoreList)
        setSortedSkuLists([])
      }

      if (formData?.section_type === 'category') {
        if (formData?.item_type === 'filters') {
          // Handle filters case - data is an object with category_id
          if (formData?.data?.category_id) {
            const filteredCategories = categories?.filter(
              (category: ManageCategoryItem) =>
                category.id === formData.data.category_id
            );
            setSortedCategoryList(filteredCategories)
          }
        } else {
          // Handle normal category case - data is an array of IDs
          const categoryIds = Array.isArray(formData?.data)
            ? formData?.data
            : [];

          const filteredCategories = categories?.filter(
            (category: ManageCategoryItem) =>
              categoryIds.includes(category.id)
          );
          setSortedCategoryList(filteredCategories)
        }
        setSortedStoreLists([])
      }

      if (editForm?.data?.type === 'sankalp_banner') {
        fetchData(editForm)
      }

      setEditFormData(editForm)
      createItemForm.setFieldsValue(editForm)
    } else {
      setTimeout(() => {
        createItemForm.resetFields()
      }, 300)
      setEditFormData({})
    }
  }, [isEditJourney, createItemForm, storeList, storeCards, categories, formData])

  const handleCreateOrUpdate = async (values: any) => {
    if (isEditJourney) {
      const id = formData?.id || ''
      try {
        await monetisationPujaApi.storesApi.updateSection(id, {
          ...values,
          active: formData?.active || false,
          id: id,
        })

        message.success('Section updated successfully')
      } catch (error) {
        showErrorToast(error)
        return
      }
    } else {
      message.loading({ content: 'Creating item...', key: 'create-section' })
      try {
        await monetisationPujaApi.storesApi.createSection(values)
        message.success({
          content: 'Section created successfully',
          duration: 2,
          key: 'create-section',
        })
      } catch (error: any) {
        showErrorToast(error)
        return
      }
    }
    createItemForm.resetFields()
    setSortedSkuLists([])
    setSortedStoreLists([])
    closeItemCreation(true)
  }

  const formSubmithandler = () => {
    createItemForm
      .validateFields()
      .then(values => {
        const formData = {
          ...values,
          position: Number(values?.position),
          ...(values?.min_app_version && {
            min_app_version: Number(values?.min_app_version),
          }),
        }
        const parsedSectionData = parseCreateSectionData(formData, storeList)
        const isMahakumbhSections = staticUIItemTypes?.map(item => item?.value?.includes(formData?.item_type)) && formData?.section_type === 'static_ui'

        if (sectionDataTypes.includes(formData?.data?.type) || isMahakumbhSections) {
          handleCreateOrUpdate(parsedSectionData)
          return
        }

        if ((values?.section_type === 'hero' || values?.section_type === 'list_item_small' || values?.section_type === 'list_item_large') && values?.item_type === 'sku') {
          if (!sortedSkuLists?.length) {
            message.error({
              content: 'Please select some Store cards.',
              duration: 3,
            })
            return
          } else {
            formData['data']['store_ids'] = sortedSkuLists?.map(
              (item: any) => item?.id,
            )
          }
        }
        if ((values?.section_type === 'hero' || values?.section_type === 'list_item_small' || values?.section_type === 'list_item_large') && values?.item_type === 'store') {
          if (!sortedStoreLists?.length) {
            message.error({
              content: 'Please select some Stores.',
              duration: 3,
            })
            return
          } else {
            formData['data']['store_ids'] = sortedStoreLists?.map(
              (item: any) => item?.id,
            )
          }
        }
        if (values?.section_type === 'category') {
          if (values?.item_type === 'filters') {
            if (!formData?.data?.category_id) {
              message.error({
                content: 'Please select a Category.',
                duration: 3,
              })
              return
            }
          } else {
            if (!sortedCategoryList?.length) {
              message.error({
                content: 'Please select some Categories.',
                duration: 3,
              })
              return
            } else {
              formData['data'] = sortedCategoryList?.map(
                (item: any) => item?.id,
              )
            }
          }
        }
        // else case
        handleCreateOrUpdate({ ...formData, target_config: formData?.target_config ? JSON.parse(formData?.target_config) : null })
      })
      .catch(errorInfo => { })
  }

  const onFormChange = (formValues: any) => {
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    if (!isEditJourney && formValues?.data?.type === 'previous_order_recommendation_card') {
      const myArray = daysOfWeek.map((day, index) => (
        {
          day,
          day_number: index,
          card_type: undefined,
          deeplink: '',
          god_name: '',
          media: {
            media_type: undefined,
            media_url: undefined,
            media_thumbnail: undefined,
          },
        }));
      createItemForm.setFieldValue(['data', 'banner_large_items'], myArray)
    }
    if (Object.keys(formValues)?.[0] === 'section_type' && formValues?.section_type !== 'data') {
      createItemForm.setFieldValue("item_type", "")
    }
    if (formValues?.section_type === 'data') {
      createItemForm.setFieldValue("item_type", "data")
    }
    if (formValues?.item_type === 'sku') {
      setSortedStoreLists([])
    }
    if (formValues?.item_type === 'store') {
      setSortedSkuLists([])
    }
  }

  const handleOnCancel = () => {
    setDandDModalVisibility(false)
  }

  const handleOnOk = (sortedList: any[]) => {
    setDandDModalVisibility(false)
    if (createItemForm.getFieldValue('section_type') === 'category') {
      setSortedCategoryList(sortedList)
      setSortedStoreLists([])
      setSortedSkuLists([])
    }
    if (createItemForm.getFieldValue('item_type') === 'sku') {
      setSortedSkuLists(sortedList)
      setSortedStoreLists([])
    }
    if (createItemForm.getFieldValue('item_type') === 'store') {
      setSortedStoreLists(sortedList)
      setSortedSkuLists([])
    }
  }

  const openSelectionWindow = () => {
    setDandDModalVisibility(true)
  }

  return (
    <>
      <SectionCreation
        visible={showItemCreation}
        onClose={closeItemCreation}
        cdnUrl={YODA_CDN_URL}
        preSignUrl={PUJA_PRESIGN_URL}
        formSubmithandler={formSubmithandler}
        form={createItemForm}
        initialValues={editFormData}
        isEditJourney={isEditJourney}
        storeList={storeList}
        onFormChange={onFormChange}
        openSelectionWindow={openSelectionWindow}
        sortedData={
          (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'sku'
            ? sortedSkuLists
            : (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'store'
              ? sortedStoreLists
              : createItemForm.getFieldValue('section_type') === 'category'
                ? sortedCategoryList
                : []
        }
        componentRender={
          (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'sku' ? (
            <ShowStoreCardInfo key={'sku'} />
          ) : (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'store' ? (
            <ShowStoreInfo key={'store'} />
          ) : (
            createItemForm.getFieldValue('section_type') === 'category'
              ? <ShowCategoryInfo key="category" />
              : ""
          )
        }
        getInventoryList={getInventoryList}
        inventoryList={inventoryList}
      />
      <DragAndDropModal
        showModal={showDandDModal}
        handleOnCancel={handleOnCancel}
        handleOnOk={handleOnOk}
        sortedData={
          (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'sku'
            ? sortedSkuLists
            : (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'store'
              ? sortedStoreLists
              : createItemForm.getFieldValue('section_type') === 'category'
                ? sortedCategoryList
                : []
        }
        itemList={
          (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'sku'
            ? storeCards
            : (createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'store'
              ? storeList?.filter((store: CreateStore) => store?.active === true)
              : createItemForm.getFieldValue('section_type') === 'category'
                ? categories
                : []
        }
        title={`Select and arrange ${createItemForm.getFieldValue(
          'item_type',
        )}`}
      >
        {createItemForm.getFieldValue('section_type') === 'category' && (
          <ShowCategoryInfo key={'category'} />
        )}
        {(createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'sku' && (
          <ShowStoreCardInfo key={'sku'} />
        )}
        {(createItemForm.getFieldValue('section_type') === 'hero' || createItemForm.getFieldValue('section_type') === 'list_item_small' || createItemForm.getFieldValue('section_type') === 'list_item_large') && createItemForm.getFieldValue('item_type') === 'store' && (
          <ShowStoreInfo key={'store'} />
        )}
      </DragAndDropModal>
    </>
  )
}

export default React.memo(CreateSection)